import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, PenLine } from 'lucide-react';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Consecutive checkboxes sharing a group label render as one row
const groupFields = (fields) => {
  const rows = [];
  fields.forEach((f) => {
    const last = rows[rows.length - 1];
    if (f.type === 'checkbox' && last?.type === 'checkbox' && last.group === f.group && last.section === f.section) {
      last.items.push(f);
    } else if (f.type === 'checkbox') {
      rows.push({ type: 'checkbox', group: f.group, section: f.section, items: [f] });
    } else {
      rows.push(f);
    }
  });
  return rows;
};

export const FormFillInFields = ({ consentId, values = {}, onChange, defaultOpen = true }) => {
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (!consentId) return;
    axios
      .get(`${backendUrl}/api/informed-consent/fields/${consentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` },
      })
      .then((r) => setFields(r.data?.data || []))
      .catch(() => setFields([]));
  }, [consentId]);

  if (!fields.length) return null;

  const set = (key, val) => onChange({ ...values, [key]: val });
  const filled = fields.filter((f) => (f.type === 'checkbox' ? values[f.key] === true : (values[f.key] || '').trim())).length;

  return (
    <div className="border border-sky-200 bg-sky-50/60 rounded-lg" data-testid={`fill-in-fields-${consentId}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid={`fill-in-toggle-${consentId}`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-sky-900">
          <PenLine className="h-4 w-4" /> Fill-In Fields (optional)
        </span>
        <span className="flex items-center gap-2 text-xs text-sky-700">
          <span data-testid={`fill-in-count-${consentId}`}>{filled} of {fields.length} filled</span>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-sky-800">
            Answers you enter appear on the patient's copy as read-only text. Leave any field empty to keep the blank line.
          </p>
          {groupFields(fields).map((row) =>
            row.type === 'checkbox' ? (
              <div key={row.items[0].key} className="space-y-1">
                {row.group && <p className="text-xs font-medium text-gray-700">{row.group}</p>}
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {row.items.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                      <Checkbox
                        checked={values[f.key] === true}
                        onCheckedChange={(c) => set(f.key, c === true)}
                        data-testid={`fill-in-checkbox-${f.key}`}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div key={row.key} className="space-y-1">
                <label className="text-xs font-medium text-gray-700">{row.label}</label>
                <Input
                  value={values[row.key] || ''}
                  onChange={(e) => set(row.key, e.target.value)}
                  placeholder="Leave blank to keep the line empty"
                  className="bg-white h-9 text-sm"
                  data-testid={`fill-in-input-${row.key}`}
                />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default FormFillInFields;
